import { Suspense, type ReactNode } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router";
import { useMe } from "@/api/hooks";
import { ErrorBoundary } from "./ErrorBoundary";
import { OfflineBanner } from "./OfflineBanner";
import { ReportProblem } from "./ReportProblem";
import { Skeleton } from "./ui";

const I = {
  today: <path d="M4 5h16v15H4zM4 9h16M9 3v4M15 3v4" />,
  plan: <path d="M4 5h16M4 12h16M4 19h10M18 17l2 2 3-4" />,
  rides: <path d="M5 18a3 3 0 1 0 0-.01M19 18a3 3 0 1 0 0-.01M5 18l4-8h6l4 8M9 10 7 6h3" />,
  trends: <path d="M4 19V5M4 19h16M7 15l4-5 3 3 5-7" />,
  health: <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />,
  settings: (
    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1-2 3.4-.2-.1a1.6 1.6 0 0 0-1.8.1 7 7 0 0 1-1.6.9 1.6 1.6 0 0 0-1 1.4V23H9.8v-.2a1.6 1.6 0 0 0-1-1.4 7 7 0 0 1-1.6-.9 1.6 1.6 0 0 0-1.8-.1l-.2.1-2-3.4.1-.1a1.6 1.6 0 0 0 .3-1.8 7 7 0 0 1 0-1.9 1.6 1.6 0 0 0-.3-1.8l-.1-.1 2-3.4.2.1a1.6 1.6 0 0 0 1.8-.1 7 7 0 0 1 1.6-.9 1.6 1.6 0 0 0 1-1.4V1h4.4v.2a1.6 1.6 0 0 0 1 1.4 7 7 0 0 1 1.6.9 1.6 1.6 0 0 0 1.8.1l.2-.1 2 3.4-.1.1a1.6 1.6 0 0 0-.3 1.8 7 7 0 0 1 0 1.9z" />
  ),
};

function Icon({ d }: { d: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {d}
    </svg>
  );
}

const NAV = [
  { to: "/", label: "Today", icon: I.today, end: true },
  { to: "/plan", label: "Plan", icon: I.plan },
  { to: "/activities", label: "Rides", icon: I.rides },
  { to: "/progress", label: "Progress", icon: I.trends },
  { to: "/health", label: "Health", icon: I.health },
  { to: "/settings", label: "Settings", icon: I.settings, desktopOnly: true },
];

export function Layout() {
  const me = useMe(); // already loaded by most pages; `onboarded` is a cheap flag
  const { pathname } = useLocation();
  const links = (mobile = false) =>
    NAV.filter((n) => !(mobile && n.desktopOnly)).map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "active" : "")}>
        <Icon d={n.icon} />
        <span>{n.label}</span>
      </NavLink>
    ));
  // a new athlete (no race yet) goes through the three-step setup first
  if (me.data && me.data.onboarded === false) return <Navigate to="/welcome" replace />;
  return (
    <div className="app">
      {/* the first Tab stop: past the navigation straight to the screen (BR-132) */}
      <a
        className="skip-link"
        href="#main"
        onClick={(e) => {
          e.preventDefault(); // not a hash change: tabs keep their own state in the hash
          document.getElementById("main")?.focus(); // focusing scrolls it into view
        }}
      >
        Skip to content
      </a>
      <nav className="sidebar" aria-label="Main">
        <div className="brand">
          <img src="/logo-mark.svg" alt="" width={24} height={24} />
          <img className="brand-word" src="/logo-word.svg" alt="Bason" width={116} height={14} />
        </div>
        {links()}
        <ReportProblem className="sidebar-report" />
      </nav>
      <header className="topbar">
        <span className="brand">
          <img src="/logo-mark.svg" alt="" width={22} height={22} />
          <img className="brand-word" src="/logo-word.svg" alt="Bason" width={108} height={13} />
        </span>
        <ReportProblem className="topbar-report" label="Report" />
        <NavLink
          to="/settings"
          aria-label="Settings"
          className={({ isActive }) => (isActive ? "active" : "")}
        >
          <Icon d={I.settings} />
        </NavLink>
      </header>
      <main className="main" id="main" tabIndex={-1}>
        <OfflineBanner />
        {/* one boundary per screen, keyed by the path: another screen starts fresh */}
        <ErrorBoundary key={pathname} where="screen">
          {/* a screen still loading keeps the shell (nav, skip link and the focus) in place */}
          <Suspense fallback={<Skeleton height={300} />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <nav className="tabbar" aria-label="Main (mobile)">
        {links(true)}
      </nav>
    </div>
  );
}
